import React, { useState, useEffect } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { Check, Close } from '@mui/icons-material';
import { lockIcon } from './common';

interface MCQProps {
  question?: string;
  options: string[];
  correctAnswerIndex: number;
  onSubmit?: (selectedAnswer: string, isCorrect: boolean) => void;
  disabled?: boolean;
  showLockIcon?: boolean;
  selectedAnswer?: string;
  isAnswered?: boolean;
}

const MCQQuestion: React.FC<MCQProps> = ({
  question,
  options,
  correctAnswerIndex,
  onSubmit,
  disabled,
  showLockIcon,
  selectedAnswer,
  isAnswered
}) => {
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (selectedAnswer) {
      const index = options.findIndex((o) => o === selectedAnswer);
      if (index >= 0) {
        setSelectedIndex(index);
        setSubmitted(isAnswered || false);
      }
    }
  }, [selectedAnswer, isAnswered, options]);

  const handleOptionClick = (index: number) => {
    if (disabled || submitted) return;

    const selectedOption = options[index];
    const isCorrect = index === correctAnswerIndex;

    setSelectedIndex(index);
    setSubmitted(true);
    onSubmit?.(selectedOption, isCorrect);
  };

  const getState = (index: number) => {
    const isSelected = selectedIndex === index;
    const isCorrect = index === correctAnswerIndex;
    const showFeedback = submitted || isAnswered;
    const correctAnswerKnown = correctAnswerIndex >= 0;

    return {
      isSelected,
      isCorrect,
      showFeedback,
      isCorrectSelected: showFeedback && isSelected && isCorrect && correctAnswerKnown,
      isWrongSelected: showFeedback && isSelected && !isCorrect && correctAnswerKnown,
      isCorrectNotSelected: showFeedback && isCorrect && !isSelected && correctAnswerKnown
    };
  };

  // Figma Colors
  const COLORS = {
    defaultBg: '#1C1C1C',
    defaultBorder: '#444444',
    dimIcon: '#868686',
    correctBg: 'rgba(0,150,45,0.20)',
    wrongBg: 'rgba(255,0,0,0.20)',
    correctBorder: '#868686',
    wrongBorder: '#868686',
    textDefault: '#B6B6B6',
    textHighlight: '#FDFDFD'
  };

  // Circle Icon Components
  const DefaultCircle = (
    <Box
      sx={{
        width: 20,
        height: 20,
        borderRadius: '50%',
        border: `2px solid ${COLORS.dimIcon}`
      }}
    />
  );

  // Filled circle shown on the option the learner selected (correct or wrong).
  // The background color (green/red) is what conveys correctness.
  const SelectedCircle = (
    <Box
      sx={{
        width: 20,
        height: 20,
        borderRadius: '50%',
        border: `2px solid ${COLORS.textHighlight}`,
        position: 'relative'
      }}
    >
      <Box
        sx={{
          width: 8,
          height: 8,
          borderRadius: '50%',
          background: COLORS.textHighlight,
          position: 'absolute',
          left: 4,
          top: 4,
          border: `2px solid ${COLORS.textHighlight}`
        }}
      />
    </Box>
  );

  return (
    <>
      {/* Title */}
      {question && (
        <Typography
          sx={{
            color: '#FDFDFD',
            fontSize: '18px',
            fontWeight: 600,
            fontFamily: 'Inter',
            mb: '20px'
          }}
        >
          {question}
        </Typography>
      )}

      <Stack spacing={1.5} width="100%">
        {options.map((option, index) => {
          const {
            isSelected,
            isCorrect,
            showFeedback,
            isCorrectSelected,
            isWrongSelected,
            isCorrectNotSelected
          } = getState(index);

          const isNeutralSelected = showFeedback && isSelected && !isCorrectSelected && !isWrongSelected;

          // Background according to Figma
          let bg = COLORS.defaultBg;
          if (isCorrectSelected || isCorrectNotSelected) bg = COLORS.correctBg;
          if (isWrongSelected) bg = COLORS.wrongBg;
          if (isNeutralSelected) bg = 'rgba(255,255,255,0.06)';

          // Border
          let border = COLORS.defaultBorder;
          if (isCorrectSelected || isCorrectNotSelected) border = COLORS.correctBorder;
          if (isWrongSelected) border = COLORS.wrongBorder;
          if (isNeutralSelected) border = '#FDFDFD';

          // Text Color
          let textColor = COLORS.textDefault;
          if (isCorrectSelected || isCorrectNotSelected) textColor = COLORS.textHighlight;
          if (isWrongSelected) textColor = COLORS.textHighlight;
          if (isNeutralSelected) textColor = COLORS.textHighlight;

          return (
            <Box
              key={index}
              onClick={() => handleOptionClick(index)}
              sx={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                padding: '10px',
                borderRadius: '4px',
                background: bg,
                outline: `1px solid ${border}`,
                outlineOffset: '-1px',
                cursor: disabled || submitted ? 'default' : 'pointer'
              }}
            >
              {/* Left icon — filled on the option the learner selected, empty otherwise */}
              <Box sx={{ width: 20, height: 20 }}>{isSelected ? SelectedCircle : DefaultCircle}</Box>

              {/* Text */}
              <Box sx={{ flex: 1 }}>
                <Typography
                  sx={{
                    fontSize: '14px',
                    fontFamily: 'Inter',
                    fontWeight: isCorrectSelected ? 700 : 400,
                    color: textColor
                  }}
                >
                  {option}
                </Typography>
              </Box>
            </Box>
          );
        })}
      </Stack>
    </>
  );
};

export default MCQQuestion;
